/**
 * What a ticket card publishes so a file dropped on it finds its row: the three ids the link needs,
 * as attributes rather than through React, because the drop is resolved from the DOM.
 */
export const TICKET_DROP_TICKET_ATTRIBUTE = "data-file-drop-ticket";
export const TICKET_DROP_PROJECT_ATTRIBUTE = "data-file-drop-project";
export const TICKET_DROP_ORG_ATTRIBUTE = "data-file-drop-org";

/**
 * Set on the card a file drag hangs over, and read by the card's own `data-[file-drop=active]`
 * classes. An attribute rather than state: the card that lights up is the only thing that changes,
 * and it changes without React rendering anything.
 */
export const TICKET_DROP_STATE_ATTRIBUTE = "data-file-drop";

/** A ticket card under a drag, with everything the link route needs to write its row. */
export interface TicketDropTarget {
	element: HTMLElement;
	ticketId: string;
	projectId: string;
	orgId: string;
}

/**
 * The ticket card a drag is over, resolved by walking up from whatever element the event landed on.
 *
 * A card that publishes only some of the three ids is no target at all: the ids come from one row,
 * so a missing one means the card is rendering something that has no row yet — a ticket queued in
 * the new-project form, or a guest board's ticket whose organisation the synced row does not carry.
 *
 * @param target - The event's target, which may be a text node, the document, or nothing.
 * @returns The card and its ids, or null when the drag is not over one.
 */
export function resolveTicketDropTarget(target: EventTarget | null): TicketDropTarget | null {
	const element = asElement(target)?.closest<HTMLElement>(`[${TICKET_DROP_TICKET_ATTRIBUTE}]`);
	if (!element) return null;
	const ticketId = element.getAttribute(TICKET_DROP_TICKET_ATTRIBUTE);
	const projectId = element.getAttribute(TICKET_DROP_PROJECT_ATTRIBUTE);
	const orgId = element.getAttribute(TICKET_DROP_ORG_ATTRIBUTE);
	if (!ticketId || !projectId || !orgId) return null;
	return { element, ticketId, projectId, orgId };
}

/**
 * The event target as something that can be walked up from. Duck-typed rather than
 * `instanceof Element`, which is both the cheaper check and the one that lets the resolver be
 * exercised under `bun test`, where there is no DOM to hold the constructor.
 */
function asElement(target: EventTarget | null) {
	if (!target) return null;
	const candidate = target as Element;
	return typeof candidate.closest === "function" ? candidate : null;
}
